/**
 * Help in the global search (Ctrl+K): articles and single tasks ("Mark a
 * missed call called back") for what staff type, next to patients and pages.
 * The corpus loads on the first query, so it never weighs on the first paint.
 */
import type { searchHelpCorpus, searchHelpSteps } from '@velora/contracts/help';
import type { SearchProvider, SearchResult } from '../../app/commands/registry.js';
import { navigateTo } from '../marketplace/palette.js';

interface HelpModule {
  searchHelpCorpus: typeof searchHelpCorpus;
  searchHelpSteps: typeof searchHelpSteps;
}
let loaded: Promise<HelpModule> | null = null;
const load = () => (loaded ??= import('@velora/contracts/help'));

/** Palette rows for a query: the best articles, then the best steps. Exported for tests. */
export function helpResults(
  help: HelpModule,
  query: string,
  open: (path: string, search?: Record<string, string>) => void = navigateTo,
): SearchResult[] {
  const q = query.trim();
  if (q.length < 3) return [];
  const articles = help.searchHelpCorpus(q, { limit: 3 }).filter((h) => h.score >= 1.2);
  const steps = help
    .searchHelpSteps(q, { limit: 4 })
    .filter((s) => s.score >= 1.5)
    // A task already shown as its article's best match is not repeated.
    .filter((s) => !articles.some((a) => a.article.id === s.article.id && a.task?.id === s.task.id))
    .slice(0, 3);
  const out: SearchResult[] = [];
  // The page the best article is about ("missed call" -> Go to Phones), so the
  // right page is one Enter away even when its name was not typed.
  const page = articles[0]?.article;
  if (page?.page && page.route !== '/login' && (articles[0]?.score ?? 0) >= 3)
    out.push({
      id: `p:${page.route}`,
      label: `Go to ${page.page}`,
      hint: `Page · ${page.title}`,
      icon: 'command',
      run: () => open(page.route),
    });
  for (const h of articles)
    out.push({
      id: `a:${h.article.id}`,
      label: h.task ? h.task.title : h.article.title,
      hint: h.task ? `Help: ${h.article.title}` : 'Help article',
      icon: 'book-open-text',
      keywords: h.article.keywords,
      run: () => open(`/help/${h.article.id}`, h.task ? { task: h.task.id } : {}),
    });
  for (const s of steps)
    out.push({
      id: `s:${s.article.id}:${s.task.id}`,
      label: s.task.title,
      hint: `Steps · ${s.article.title}`,
      icon: 'list-checks',
      run: () => open(`/help/${s.article.id}`, { task: s.task.id }),
    });
  return out;
}

export const helpSearchProvider: SearchProvider = {
  id: 'help',
  heading: 'Help',
  minLength: 3,
  order: 80,
  patients: false,
  async search(query) {
    return helpResults(await load(), query);
  },
};
